import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useVehicleMetrics } from "@/hooks/useVehicleMetrics";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

function formatCurrency(value: number) {
  return value < 0
    ? `-$${Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getOverallVerdict(metrics: { totalEarnings: number; totalExpenses: number }[]) {
  const totalEarnings = metrics.reduce((s, m) => s + m.totalEarnings, 0);
  const totalExpenses = metrics.reduce((s, m) => s + m.totalExpenses, 0);
  const net = totalEarnings - totalExpenses;

  if (totalEarnings === 0 && totalExpenses === 0) return { label: "Unclear", color: "text-muted-foreground" };
  if (net > 0) return { label: "Profitable", color: "text-primary" };
  if (net === 0) return { label: "Break-even", color: "text-warning" };
  return { label: "Unclear", color: "text-muted-foreground" };
}

export default function BasicSignals() {
  const { vehicleMetrics, isLoading, hasVehicles } = useVehicleMetrics();

  const verdict = hasVehicles ? getOverallVerdict(vehicleMetrics) : null;

  return (
    <AppLayout>
      <div className="p-4 md:p-8 space-y-6 max-w-2xl">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Basic Signals</h1>
          <p className="text-muted-foreground mt-1">
            A simple snapshot of what your numbers say so far.
          </p>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-36" />
            ))}
          </div>
        ) : !hasVehicles ? (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground">
              <p>Add a vehicle and some data first to see signals here.</p>
              <Link to="/app/vehicles" className="text-primary underline underline-offset-2 text-sm mt-2 inline-block">
                Go to Vehicles
              </Link>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Per-vehicle snapshots */}
            {vehicleMetrics.map((v) => {
              const net = v.totalEarnings - v.totalExpenses;
              const tooEarly = v.totalEarnings === 0 && v.totalExpenses === 0;

              return (
                <Card key={v.vehicleId}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                      <Link to="/app/vehicles" className="hover:underline underline-offset-2">
                        {v.vehicleName}
                      </Link>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {tooEarly ? (
                      <p className="text-sm text-muted-foreground italic">
                        Too early to tell — add some earnings or expenses for this vehicle.
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">You've earned</p>
                          <p className="text-lg font-semibold">{formatCurrency(v.totalEarnings)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">You've spent</p>
                          <p className="text-lg font-semibold">{formatCurrency(v.totalExpenses)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">What this means so far</p>
                          <p
                            className={cn(
                              "text-lg font-bold",
                              net > 0 ? "text-primary" : net < 0 ? "text-destructive" : "text-muted-foreground"
                            )}
                          >
                            {formatCurrency(net)}
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {/* Aggregate verdict */}
            {verdict && (
              <Card className="border-muted">
                <CardContent className="pt-6 text-center">
                  <p className="text-sm text-muted-foreground mb-1">So far, your hosting is:</p>
                  <p className={cn("text-xl font-bold", verdict.color)}>{verdict.label}</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
